import React from "react";
import PageShell from "@/components/site/PageShell";
import PageHeader from "@/components/site/PageHeader";
import FeedbackLoops from "@/components/home/FeedbackLoops";
import { MapaInvestigacao } from "@/components/site/MapaInvestigacao";
import { Container, Eyebrow, H2, Body, ArrowLink, Reveal, EpistemicTag } from "@/components/site/ui";
import { PQ, PQ_IMG } from "@/content/pesquisa";

const Block = ({ id, eyebrow, title, tag, children, light = false }) => (
    <section data-section-id={id} data-testid={`pesquisa-${id}`} className={`relative py-20 md:py-28 ${light ? "bg-[#F2EFE5] text-[#0E2018]" : "border-t border-[#1B3025]"}`}>
        {light && <div aria-hidden="true" className="absolute inset-0 paper-grain" />}
        <Container className="relative">
            <Reveal className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-3 mb-5">
                    <Eyebrow light={light} className="!mb-0">{eyebrow}</Eyebrow>
                    {tag && <EpistemicTag tipo={tag} light={light} />}
                </div>
                {title && <H2 light={light}>{title}</H2>}
            </Reveal>
            <Reveal delay={120} className="mt-10">{children}</Reveal>
        </Container>
    </section>
);

const Perguntas = ({ lista, light, testId }) => (
    <ul data-testid={testId} className="flex flex-col">
        {lista.map((q) => (
            <li key={q} className={`py-4 border-t font-serif italic font-light text-[18px] md:text-[19px] leading-[1.45] ${light ? "border-[#0E2018]/15" : "border-[#1B3025] text-[#F2EFE5]"}`}>{q}</li>
        ))}
    </ul>
);

const PhotoSection = ({ id, img, alt, children }) => (
    <section data-section-id={id} data-testid={`pesquisa-${id}`} className="relative py-24 md:py-32 overflow-hidden">
        <img src={img} alt={alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover object-[75%_center]" style={{ filter: "saturate(0.6) brightness(0.8)" }} />
        <div aria-hidden="true" className="absolute inset-0 bg-[#0A1812]/80 md:bg-[#0A1812]/45" />
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-[#0A1812] via-[#0A1812]/90 to-[#0A1812]/60" />
        <Container className="relative">{children}</Container>
    </section>
);

const Investigacao = () => (
    <Block id="investigacao" eyebrow="Pesquisa" title={PQ.investigacao.title} light>
        <div className="max-w-3xl flex flex-col gap-5">{PQ.investigacao.p.map((p) => <Body key={p.slice(0, 24)} light>{p}</Body>)}</div>
        <div data-testid="pesquisa-pergunta-ampla" className="mt-12 pt-8 border-t border-[#0E2018]/15 max-w-3xl">
            <p className="font-sans text-[10px] uppercase tracking-[0.26em] text-[#6b6a52]">{PQ.investigacao.amplaLabel}</p>
            <p className="mt-3 font-serif italic font-light text-[17px] md:text-[19px] leading-[1.55] text-[#2d3a31]">{PQ.investigacao.ampla}</p>
        </div>
    </Block>
);

const Nucleo = () => (
    <Block id="nucleo" eyebrow={PQ.nucleo.eyebrow} title={PQ.nucleo.title}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5 flex flex-col gap-5">{PQ.nucleo.p.map((p) => <Body key={p.slice(0, 24)}>{p}</Body>)}</div>
            <div className="lg:col-span-7 border-l-2 border-[#C7A659] pl-6 md:pl-10">
                <div className="flex flex-wrap items-center gap-3">
                    <p className="font-sans text-[10.5px] uppercase tracking-[0.26em] text-[#C7A659]">{PQ.nucleo.questaoLabel}</p>
                </div>
                <p data-testid="pesquisa-questao" className="mt-5 font-serif font-light text-[24px] md:text-[32px] leading-[1.3] text-[#F2EFE5] text-balance">{PQ.nucleo.questao}</p>
                <p className="mt-5 font-sans font-light text-[13px] leading-[1.7] text-[#7E8978]">{PQ.nucleo.questaoNota}</p>
            </div>
        </div>
        <div data-testid="pesquisa-adaptacao" className="mt-20 pt-12 border-t border-[#1B3025] grid grid-cols-1 lg:grid-cols-12 gap-10">
            <h3 className="lg:col-span-4 font-serif font-light text-[24px] md:text-[28px] leading-[1.25] text-[#F2EFE5]">{PQ.adaptacao.title}</h3>
            <div className="lg:col-span-8 flex flex-col gap-5 max-w-2xl">{PQ.adaptacao.p.map((p) => <Body key={p.slice(0, 24)}>{p}</Body>)}</div>
        </div>
    </Block>
);

const Nivel = ({ d, testId }) => (
    <div data-testid={testId} className="py-8 border-t border-[#0E2018]/15">
        <div className="flex flex-wrap items-center gap-3">
            <p className="font-sans text-[10.5px] uppercase tracking-[0.26em] text-[#8a6a22]">{d.label}</p>
        </div>
        <p className="mt-4 font-serif font-light text-[19px] md:text-[22px] leading-[1.5]">{d.text}</p>
        <p className="mt-4 font-sans font-light italic text-[13px] leading-[1.7] text-[#6b6a52]">{d.nota}</p>
    </div>
);

const TeseHipotese = () => (
    <Block id="proposicao" eyebrow="Tese e hipótese" light>
        <div className="max-w-4xl">
            <Nivel d={PQ.tese} testId="pesquisa-tese" />
            <Nivel d={PQ.hipotese} testId="pesquisa-hipotese" />
        </div>
        <div className="mt-20"><MapaInvestigacao m={PQ.mapa} /></div>
    </Block>
);

const Complexidade = () => (
    <Block id="complexidade" eyebrow={PQ.complexidade.eyebrow} title={PQ.complexidade.title} tag="lente-teorica">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div className="flex flex-col gap-6 max-w-xl">
                <Body>{PQ.complexidade.text}</Body>
                <p className="font-serif italic text-[19px] leading-[1.5] text-[#F2EFE5] border-l-2 border-[#C7A659] pl-5">{PQ.complexidade.pergunta}</p>
                <p className="font-serif text-[17px] text-[#C7A659]">{PQ.complexidade.naoMede}</p>
            </div>
            <div className="max-w-[440px] w-full mx-auto">
                <div className="bg-[#F2EFE5] rounded-sm p-6"><FeedbackLoops /></div>
                <p data-testid="pesquisa-diagrama-nota" className="mt-3 text-center font-sans text-[11px] text-[#7E8978]">{PQ.complexidade.nota}</p>
                <p data-testid="pesquisa-diagrama-nota2" className="mt-1 text-center font-sans text-[10px] text-[#7E8978]/80">{PQ.complexidade.nota2}</p>
            </div>
        </div>
    </Block>
);

const Contexto = () => (
    <PhotoSection id="contexto" img={PQ_IMG.contexto} alt="Vista aérea noturna de cidade ribeirinha entre floresta, porto e vias iluminadas">
        <Reveal className="max-w-3xl">
            <Eyebrow>{PQ.contexto.eyebrow}</Eyebrow>
            <H2>{PQ.contexto.title}</H2>
            <div className="mt-8 flex flex-col gap-5 max-w-2xl">{PQ.contexto.p.map((p) => <Body key={p.slice(0, 24)}>{p}</Body>)}</div>
        </Reveal>
    </PhotoSection>
);

const Circular = () => (
    <PhotoSection id="circular" img={PQ_IMG.circular} alt="Pequena oficina de reaproveitamento de materiais iluminada por uma lâmpada">
        <Reveal className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-3 mb-5">
                <Eyebrow className="!mb-0">{PQ.circular.eyebrow}</Eyebrow>
            </div>
            <H2>{PQ.circular.title}</H2>
        </Reveal>
        <Reveal delay={120} className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-14">
            <Body className="max-w-xl">{PQ.circular.text}</Body>
            <Perguntas lista={PQ.circular.perguntas} testId="pesquisa-circular-perguntas" />
        </Reveal>
    </PhotoSection>
);

const Tecnologia = () => (
    <Block id="tecnologia" eyebrow={PQ.tecnologia.eyebrow} title={PQ.tecnologia.title}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
            <Body className="max-w-xl">{PQ.tecnologia.text}</Body>
            <Perguntas lista={PQ.tecnologia.perguntas} testId="pesquisa-tecnologia-perguntas" />
        </div>
    </Block>
);

const Humano = () => (
    <Block id="humano" eyebrow={PQ.humano.eyebrow} title={PQ.humano.title}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
            <Body className="max-w-xl">{PQ.humano.text}</Body>
            <p className="font-serif italic font-light text-[20px] md:text-[23px] leading-[1.45] text-[#F2EFE5] border-l-2 border-[#C7A659] pl-6">{PQ.humano.pergunta}</p>
        </div>
    </Block>
);

const Caverna = () => (
    <section data-section-id="limites" data-testid="pesquisa-limites" className="relative bg-[#F2EFE5] text-[#0E2018] overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 paper-grain" />
        <div className="relative grid grid-cols-1 lg:grid-cols-12">
            <div className="relative lg:col-span-4 min-h-[30svh] lg:min-h-full">
                <img src={PQ_IMG.caverna} alt="Sombras humanas projetadas por fogo na parede de uma caverna" loading="lazy" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "saturate(0.6) sepia(0.15)" }} />
            </div>
            <Container className="lg:col-span-8 py-20 md:py-28 lg:pl-16 max-w-none">
                <Reveal>
                    <div className="flex flex-wrap items-center gap-3 mb-5">
                        <Eyebrow light className="!mb-0">{PQ.caverna.eyebrow}</Eyebrow>
                        <EpistemicTag tipo="lente-filosofica" light />
                    </div>
                    <H2 light>{PQ.caverna.pergunta}</H2>
                    <Body light className="mt-8 max-w-2xl">{PQ.caverna.text}</Body>
                    <p className="mt-6 font-serif text-[18px] leading-[1.55] max-w-2xl border-l-2 border-[#C7A659] pl-5">{PQ.caverna.investigador}</p>
                </Reveal>
            </Container>
        </div>
    </section>
);

const Valor = () => (
    <section data-section-id="valor" data-testid="pesquisa-valor" className="py-16 md:py-20 border-t border-[#1B3025]">
        <Container>
            <Reveal className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-4">
                    <Eyebrow>{PQ.valor.eyebrow}</Eyebrow>
                    <h2 className="font-serif font-light text-[26px] md:text-[32px] leading-[1.2] text-[#F2EFE5]">{PQ.valor.title}</h2>
                </div>
                <div className="lg:col-span-8 flex flex-col gap-6 max-w-2xl">
                    <Body>{PQ.valor.text}</Body>
                    <p className="font-serif italic font-light text-[19px] md:text-[21px] leading-[1.45] text-[#F2EFE5] border-l-2 border-[#C7A659] pl-6">{PQ.valor.pergunta}</p>
                </div>
            </Reveal>
        </Container>
    </section>
);

const Questoes = () => (
    <Block id="questoes" eyebrow="Em aberto" title={PQ.questoes.title} light>
        <div className="max-w-3xl"><Perguntas lista={PQ.questoes.lista} light testId="pesquisa-questoes-lista" /></div>
    </Block>
);

const Fecho = () => (
    <section data-section-id="principio" className="py-20 md:py-24 border-t border-[#1B3025]">
        <Container className="max-w-4xl">
            <Reveal>
                <div data-testid="legenda-epistemica">
                    <p className="font-sans text-[10px] uppercase tracking-[0.28em] text-[#7E8978] mb-4">Como ler esta página — tipos de conhecimento</p>
                    <div className="flex flex-wrap gap-2">{PQ.legenda.map((k) => <EpistemicTag key={k} tipo={k} />)}</div>
                </div>
                <p className="mt-12 font-serif italic text-[18px] text-[#D7D1BF]">{PQ.docencia}</p>
                <div className="mt-8 flex flex-wrap gap-10">
                    <ArrowLink to="/publicacoes" testId="pesquisa-link-publicacoes">Publicações</ArrowLink>
                    <ArrowLink to="/docencia" testId="pesquisa-link-docencia">Docência</ArrowLink>
                    <ArrowLink to="/contato" testId="pesquisa-link-contato">Cooperação</ArrowLink>
                </div>
            </Reveal>
        </Container>
    </section>
);

const PesquisaPage = () => (
    <PageShell path="/pesquisa" title="Pesquisa" sections={["header", "investigacao", "nucleo", "proposicao", "complexidade", "contexto", "circular", "valor", "tecnologia", "humano", "limites", "questoes", "principio"]}>
        <PageHeader testId="pesquisa-page-header" eyebrow={PQ.header.eyebrow} title={PQ.header.title} lead={PQ.header.lead} lead2={PQ.header.lead2} img={PQ_IMG.header} veil={0.8} alt="Bancada de uma pequena oficina com materiais reaproveitados e um notebook, à meia-luz" />
        <Investigacao />
        <Nucleo />
        <TeseHipotese />
        <Complexidade />
        <Contexto />
        <Circular />
        <Valor />
        <Tecnologia />
        <Humano />
        <Caverna />
        <Questoes />
        <Fecho />
    </PageShell>
);

export default PesquisaPage;
